const zeroPad = (num) => {
    return ('' + num).padStart(2, '0');
};
const unique = (v, i, a) => a.indexOf(v) === i;
/*
 * https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
 */
const notEmpty = (value) => {
    return value !== null && value !== undefined;
};
const uniq = (v, i, a) => {
    if (v) {
        return unique(v, i, a);
    }
    return false;
};
const getValue = (object, key) => {
    return object[key];
};
// https://dev.to/phibya/methods-to-get-unique-values-from-arrays-in-javascript-and-their-performance-1da8
const uniqueObjects = (objects, uniqueBy, keepFirst = true) => {
    return Array.from(objects
        .reduce((map, e) => {
        const key = uniqueBy
            // @ignore
            .map((key) => [getValue(e, key), typeof getValue(e, key)])
            .flat()
            .join('-');
        if (keepFirst && map.has(key))
            return map;
        return map.set(key, e);
    }, new Map())
        .values());
};
const sortAndReindex = (array, orderField) => {
    const sortedArray = array.sort((a, b) => {
        return (a[orderField] ?? 0) - (b[orderField] ?? 0);
    });
    sortedArray.forEach((record, index) => {
        record[orderField] = index;
    });
    return sortedArray;
};
const moveItemUp = (data, index, orderField) => {
    if (index <= 0 || index >= data.length) {
        return data;
    }
    const newData = [...data];
    const temp = newData[index];
    newData[index] = newData[index - 1];
    newData[index - 1] = temp;
    // Update order fields for the swapped items
    newData[index] = { ...newData[index], [orderField]: index };
    newData[index - 1] = { ...newData[index - 1], [orderField]: index - 1 };
    return sortAndReindex(newData, orderField);
};
const moveItemDown = (data, index, orderField) => {
    if (index < 0 || index >= data.length - 1) {
        return data;
    }
    const newData = [...data];
    const temp = newData[index];
    newData[index] = newData[index + 1];
    newData[index + 1] = temp;
    // Update order fields for the swapped items
    newData[index] = { ...newData[index], [orderField]: index };
    newData[index + 1] = { ...newData[index + 1], [orderField]: index + 1 };
    return sortAndReindex(newData, orderField);
};
const diffInObjects = (a, b, debug) => {
    const hasDiff = !deepEqual(a, b);
    if (debug && hasDiff) {
        const differences = findDifferences(a, b);
        console.log(`[${debug}] diffInObjects found differences:`, differences);
    }
    return hasDiff;
};
const diffInArrays = (a, b, debug) => {
    const hasDiff = !deepEqual(a, b);
    if (debug && hasDiff) {
        const differences = findDifferences(a, b);
        console.log(`[${debug}] diffInArrays found differences:`, differences);
    }
    return hasDiff;
};
const findDifferences = (a, b, path = '') => {
    const results = [];
    const typeA = typeof a;
    const typeB = typeof b;
    // Handle undefined/null
    if (a === undefined && b !== undefined) {
        results.push({ path: path || 'root', valueA: a, valueB: b, type: 'added' });
        return results;
    }
    if (a !== undefined && b === undefined) {
        results.push({ path: path || 'root', valueA: a, valueB: b, type: 'removed' });
        return results;
    }
    if (a === null && b === null) {
        return results;
    }
    if (a === null || b === null) {
        results.push({ path: path || 'root', valueA: a, valueB: b, type: 'changed' });
        return results;
    }
    // Type mismatch
    if (typeA !== typeB) {
        results.push({
            path: path || 'root',
            valueA: a,
            valueB: b,
            type: 'type_mismatch'
        });
        return results;
    }
    // Functions and undefined are considered equal
    if (typeA === 'function' || typeA === 'undefined') {
        return results;
    }
    // Arrays
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            results.push({
                path: path ? `${path}.length` : 'length',
                valueA: a.length,
                valueB: b.length,
                type: 'changed'
            });
        }
        const maxLen = Math.max(a.length, b.length);
        for (let i = 0; i < maxLen; i++) {
            const itemPath = path ? `${path}[${i}]` : `[${i}]`;
            if (i >= a.length) {
                results.push({ path: itemPath, valueA: undefined, valueB: b[i], type: 'added' });
            }
            else if (i >= b.length) {
                results.push({ path: itemPath, valueA: a[i], valueB: undefined, type: 'removed' });
            }
            else {
                results.push(...findDifferences(a[i], b[i], itemPath));
            }
        }
        return results;
    }
    // Objects
    if (typeA === 'object') {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);
        const allKeys = [...new Set([...keysA, ...keysB])];
        for (const key of allKeys) {
            const keyPath = path ? `${path}.${key}` : key;
            const valA = a[key];
            const valB = b[key];
            // Skip if both values are undefined (structural diff but no value diff)
            if (valA === undefined && valB === undefined) {
                continue;
            }
            if (!(key in a)) {
                results.push({ path: keyPath, valueA: undefined, valueB: valB, type: 'added' });
            }
            else if (!(key in b)) {
                results.push({ path: keyPath, valueA: valA, valueB: undefined, type: 'removed' });
            }
            else {
                results.push(...findDifferences(valA, valB, keyPath));
            }
        }
        return results;
    }
    // Primitives
    if (Number.isNaN(a) && Number.isNaN(b)) {
        return results;
    }
    if (a !== b) {
        results.push({ path: path || 'root', valueA: a, valueB: b, type: 'changed' });
    }
    return results;
};
const deepEqual = (a, b) => {
    const seenA = [];
    const seenB = [];
    const deepInner = (a, b) => {
        const typeA = typeof a;
        const typeB = typeof b;
        if (typeA !== typeB) {
            return false;
        }
        // no more comparison on functions and undefined
        if (typeA === 'function' || typeA === 'undefined') {
            return true;
        }
        if (typeA === 'object') {
            // Null
            if (a === null || b === null) {
                return a === b;
            }
            if (seenA.indexOf(a) !== -1 || seenB.indexOf(b) !== -1) {
                return seenA.indexOf(a) !== -1 && seenB.indexOf(b) !== -1;
            }
            seenA.push(a);
            seenB.push(b);
            // Arrays
            if (Array.isArray(a) || Array.isArray(b)) {
                if (!Array.isArray(a) || !Array.isArray(b)) {
                    return false;
                }
                return (a.length === b.length &&
                    a.every((item, index) => deepInner(item, b[index])));
            }
            // Proper objects - filter out keys with undefined values for comparison
            const keysA = Object.keys(a).filter((k) => a[k] !== undefined);
            const keysB = Object.keys(b).filter((k) => b[k] !== undefined);
            if (keysA.length !== keysB.length) {
                return false;
            }
            return keysA.every((k) => k in b && deepInner(a[k], b[k]));
        }
        // Primitives
        if (Number.isNaN(a) && Number.isNaN(b)) {
            return true;
        }
        return a === b;
    };
    return deepInner(a, b);
};
const replacer = (_, value) => {
    return Number.isNaN(value) ? 'NaN_Replacement' : value;
};
const parser = (_, val) => {
    return val === 'NaN_Replacement' ? NaN : val;
};
function clone(instance) {
    if (instance == null) {
        return instance;
    }
    return JSON.parse(JSON.stringify(instance, replacer), parser);
}
function isWholeInteger(str) {
    return /^-?\d+$/.test(str);
}
const toNumber = (n, def = NaN) => {
    if (typeof n === 'string') {
        // parseInt will return the number even if it has rubbish at the end
        if (isWholeInteger(n)) {
            const result = parseInt(n, 10);
            return isNaN(result) ? def : result;
        }
        else {
            return def;
        }
    }
    if (typeof n === 'number') {
        return isNaN(n) ? def : n;
    }
    return def;
};
const joinWithRepeatedFormat = (tekst) => {
    const testCounts = tekst.reduce((acc, txt) => {
        acc[txt] = (acc[txt] || 0) + 1;
        return acc;
    }, {});
    return Object.entries(testCounts)
        .sort(([, count]) => -count)
        .map(([holder, count]) => (count > 1 ? `${count}x${holder}` : `${holder}`))
        .join(', ');
};
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const uuidv4 = () => {
    return crypto.randomUUID();
};
export { uuidv4, toNumber, zeroPad, isPlainObject, unique, notEmpty, uniq, uniqueObjects, clone, sortAndReindex, moveItemUp, moveItemDown, diffInArrays, diffInObjects, findDifferences, deepEqual, joinWithRepeatedFormat };
//# sourceMappingURL=utils.js.map